import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Clock, KeyRound } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useDevices } from '@/hooks/useDevices';
import { useDeviceStore } from '@/stores/deviceStore';
import { cn } from '@/lib/utils';

interface KeyHistoryEntry {
  id: string;
  access_method: string;
  status: string;
  created_at: string;
}

/**
 * HistoryPage — key history per device.
 *
 * A horizontal device chip menu (defaulting to the active device) selects
 * which device's history is shown, so the scope is always visible instead
 * of following a hidden global selection.
 */
export default function HistoryPage() {
  const { user } = useAuth();
  const { activeDeviceId, assignments } = useDeviceStore();
  useDevices();

  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const deviceId = selectedDeviceId ?? activeDeviceId;

  const devices = useMemo(
    () =>
      assignments
        .filter((a) => a.device)
        .map((a) => a.device!)
        .filter((d, i, arr) => arr.findIndex((x) => x.id === d.id) === i),
    [assignments]
  );

  const { data: history, isLoading } = useQuery({
    queryKey: ['key-history', user?.id, deviceId],
    queryFn: async (): Promise<KeyHistoryEntry[]> => {
      if (!user || !deviceId) return [];

      const { data, error } = await supabase
        .from('keys_v1')
        .select('id, access_method, status, created_at')
        .eq('user_id', user.id)
        .eq('device_id', deviceId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data ?? []) as KeyHistoryEntry[];
    },
    enabled: !!user && !!deviceId,
  });

  const selectedDevice = useMemo(
    () => devices.find((d) => d.id === deviceId) ?? null,
    [devices, deviceId]
  );

  // Klockslag först, dag efter — t.ex. "14:32 · mån 6 juli".
  const formatTimestamp = (iso: string) => {
    const d = new Date(iso);
    const time = d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
    const day = d.toLocaleDateString('sv-SE', {
      weekday: 'short',
      day: 'numeric',
      month: 'long',
    });
    return `${time} · ${day}`;
  };

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 px-4 py-6 space-y-5">
        <h2 className="text-2xl font-semibold tracking-tight">Historik</h2>

        {/* Device selector chips */}
        {devices.length > 1 && (
          <div className="flex gap-2 overflow-x-auto scrollbar-none -mx-4 px-4 py-1">
            {devices.map((device) => {
              const isActive = device.id === deviceId;
              return (
                <button
                  key={device.id}
                  onClick={() => setSelectedDeviceId(device.id)}
                  className={cn(
                    'shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-all active:scale-95',
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-[0_6px_20px_-6px_hsl(var(--glass-shadow))]'
                      : 'glass-chrome text-foreground/80 hover:text-foreground'
                  )}
                >
                  {device.name}
                </button>
              );
            })}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full rounded-2xl" />
            ))}
          </div>
        ) : history && history.length > 0 ? (
          <div className="space-y-2">
            {history.map((entry) => (
              <Card key={entry.id} className="glass-thin rounded-2xl">
                <CardContent className="flex items-center justify-between p-3.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--foreground)/0.06)] ring-1 ring-[hsl(var(--glass-stroke))]">
                      <KeyRound className="h-4 w-4 text-foreground/80" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">
                        {selectedDevice?.name ?? 'Enhet'}
                      </p>
                      <p className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3 shrink-0" />
                        {formatTimestamp(entry.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Badge
                      variant={entry.status === 'active' ? 'default' : 'secondary'}
                      className="text-[10px]"
                    >
                      {entry.status === 'active' ? 'Aktiv' : 'Utgången'}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {entry.access_method?.toUpperCase() || 'QR'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-muted-foreground">
              {deviceId ? 'Ingen historik ännu' : 'Välj en enhet för att se historik'}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
